import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Clock,
  MapPin,
  Phone,
  Pill,
  Calendar,
  HelpCircle,
  ChevronRight,
  Shield,
  ShoppingBag
} from 'lucide-react';

const PharmacyChatbot = ({ onNavigateToCatalog }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Bonjour ! 👋 Je suis l\'assistant virtuel de la Pharmacie Saint Ange Des Amis. Comment puis-je vous aider ?',
      time: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Quick action buttons
  const quickActions = [
    { id: 'horaires', label: 'Horaires', icon: Clock },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'catalogue', label: 'Catalogue', icon: ShoppingBag },
    { id: 'livraison', label: 'Livraison', icon: MapPin },
    { id: 'assurance', label: 'Assurance', icon: Shield },
  ];

  // Automated responses database
  const getAutomatedResponse = (query) => {
    const lowerQuery = query.toLowerCase();
    
    // Horaires
    if (lowerQuery.includes('horaire') || lowerQuery.includes('ouvert') || lowerQuery.includes('fermé') || lowerQuery.includes('heure')) {
      return {
        text: '🕐 **Nos horaires d\'ouverture :**\n\n• Lundi - Vendredi : 7h30 - 20h00\n• Samedi : 7h30 - 13h00\n• Dimanche : Fermé\n\nNous sommes là pour vous accueillir !',
        followUp: ['contact', 'livraison']
      };
    }
    
    // Contact (Adresse + Téléphone combinés)
    if (lowerQuery.includes('adresse') || lowerQuery.includes('où') || lowerQuery.includes('localisation') || lowerQuery.includes('trouver') || lowerQuery.includes('gps') || lowerQuery.includes('itinéraire') || lowerQuery.includes('téléphone') || lowerQuery.includes('appeler') || lowerQuery.includes('numéro') || lowerQuery.includes('contact') || lowerQuery.includes('joindre')) {
      return {
        text: '📍 **Notre adresse :**\nAngre 7e tranche carrefour Bluetooth\nAbidjan, Côte d\'Ivoire\n\n📞 **Téléphones :**\n• Mobile : **07 78 41 52 77**\n• Fixe : **27 22 30 05 72**\n\n🗺️ [Ouvrir dans Google Maps](https://www.google.com/maps/place/92V7%2BH8+Abidjan)',
        followUp: ['horaires', 'livraison']
      };
    }
    
    // Réservation
    if (lowerQuery.includes('réserv') || lowerQuery.includes('command') || lowerQuery.includes('click') || lowerQuery.includes('collect') || lowerQuery.includes('retrait')) {
      return {
        text: '🛒 **Comment réserver ?**\n\n1. Parcourez notre catalogue en ligne\n2. Ajoutez les produits au panier\n3. Validez votre réservation\n4. Récupérez en pharmacie\n\n⚠️ **Important :** Le paiement se fait uniquement sur place au moment du retrait.\n\n👉 Accédez au catalogue depuis la page d\'accueil !',
        followUp: ['horaires', 'medicament']
      };
    }
    
    // Médicament / Disponibilité - IMPROVED FOR CATALOG LINK
    if (lowerQuery.includes('médicament') || lowerQuery.includes('disponib') || lowerQuery.includes('stock') || lowerQuery.includes('avez-vous') || lowerQuery.includes('avez vous') || lowerQuery.includes('ce produit') || lowerQuery.includes('en pharmacie') || lowerQuery.includes('cherche') || lowerQuery.includes('trouver') || lowerQuery.includes('existe')) {
      return {
        text: '💊 **Disponibilité des produits**\n\nVous pouvez consulter la disponibilité de nombreux produits dans notre **catalogue en ligne**.\n\n👉 **[Cliquez ici pour voir le catalogue](#catalog)**\n\nSi vous ne trouvez pas le produit recherché, vous pouvez également :\n• Utiliser le **formulaire de demande de produit** sur la page catalogue\n• Nous appeler au **07 78 41 52 77**\n\nNous vous répondrons rapidement !',
        followUp: ['reservation', 'telephone'],
        hasCatalogLink: true
      };
    }
    
    // Produit spécifique
    if (lowerQuery.includes('vitamine') || lowerQuery.includes('doliprane') || lowerQuery.includes('paracétamol') || lowerQuery.includes('ibuprofène') || lowerQuery.includes('aspirine') || lowerQuery.includes('sirop') || lowerQuery.includes('crème') || lowerQuery.includes('pommade')) {
      return {
        text: '🔍 **Recherche de produit**\n\nPour vérifier la disponibilité de ce produit :\n\n1. Consultez notre **catalogue en ligne** 👉 **[Voir le catalogue](#catalog)**\n2. Utilisez la barre de recherche pour trouver votre produit\n3. Si non trouvé, remplissez le formulaire de demande\n\nOu appelez-nous au **07 78 41 52 77** pour une réponse immédiate !',
        followUp: ['telephone', 'horaires'],
        hasCatalogLink: true
      };
    }
    
    // Prix
    if (lowerQuery.includes('prix') || lowerQuery.includes('coût') || lowerQuery.includes('tarif') || lowerQuery.includes('combien')) {
      return {
        text: '💰 **Nos prix**\n\nTous les prix sont affichés dans notre catalogue en ligne. Ce sont des prix indicatifs.\n\nLe prix final vous sera confirmé au moment du retrait en pharmacie.\n\n👉 Consultez notre catalogue pour voir les prix !',
        followUp: ['reservation', 'medicament']
      };
    }
    
    // Conseil / Pharmacien
    if (lowerQuery.includes('conseil') || lowerQuery.includes('pharmacien') || lowerQuery.includes('avis') || lowerQuery.includes('recommand')) {
      return {
        text: '👨‍⚕️ **Besoin d\'un conseil ?**\n\nNos pharmaciens sont à votre disposition pour vous conseiller.\n\n• En pharmacie pendant nos heures d\'ouverture\n• Par téléphone au **07 78 41 52 77**\n\n⚠️ Pour toute question médicale, consultez toujours un professionnel de santé.',
        followUp: ['telephone', 'horaires']
      };
    }
    
    // Ordonnance
    if (lowerQuery.includes('ordonnance') || lowerQuery.includes('prescription')) {
      return {
        text: '📋 **Médicaments sur ordonnance**\n\nLes médicaments nécessitant une ordonnance ne peuvent pas être réservés en ligne.\n\nPrésentez-vous directement en pharmacie avec votre ordonnance.\n\n⚠️ N\'envoyez jamais votre ordonnance par message.',
        followUp: ['horaires', 'adresse']
      };
    }
    
    // Livraison
    if (lowerQuery.includes('livraison') || lowerQuery.includes('livrer') || lowerQuery.includes('domicile')) {
      return {
        text: '🚚 **Livraison**\n\nNous ne proposons pas de livraison à domicile.\n\n**Cependant**, vous pouvez envoyer votre chauffeur récupérer votre préparation !\n\n1. Réservez vos produits sur le site (Click & Collect)\n2. Envoyez votre chauffeur avec le numéro de réservation\n3. Il récupère et paie sur place (ou règlement à l\'avance facultatif via Wave / Orange Money)\n\n👉 Pratique et rapide !',
        followUp: ['reservation', 'adresse']
      };
    }
    
    // Paiement
    if (lowerQuery.includes('paiement') || lowerQuery.includes('payer') || lowerQuery.includes('carte') || lowerQuery.includes('espèce') || lowerQuery.includes('mobile money') || lowerQuery.includes('wave')) {
      return {
        text: '💳 **Modes de paiement acceptés**\n\n• **Espèces** au comptoir\n• **Carte bancaire** au comptoir\n• **Mobile Money** (Orange Money, MTN, Moov)\n• **Wave**\n\n💡 **Règlement à l\'avance facultatif (sous conditions)** : pour les **produits de parapharmacie** uniquement, vous pouvez régler à l\'avance via Wave ou Orange Money — c\'est optionnel et soumis à validation par notre équipe.\n\n⚠️ Pour les **médicaments**, aucun règlement en ligne n\'est possible : règlement uniquement au comptoir, après validation pharmaceutique. Tout paiement en avance reste provisoire jusqu\'à la délivrance. Remboursement intégral garanti en cas de non-délivrance.',
        followUp: ['reservation', 'horaires']
      };
    }
    
    // Assurance
    if (lowerQuery.includes('assurance') || lowerQuery.includes('mutuelle') || lowerQuery.includes('tiers payant') || lowerQuery.includes('prise en charge')) {
      return {
        text: '🏥 **Assurances prises en charge**\n\nNous acceptons les assurances et mutuelles suivantes :\n\n• ALLIANZ • AMAT-CI • AMGS SANTE • AMI\n• AMSA • ANKARA • ASCOMA\n• ATLANTIQUE ASSURANCE • AXA\n• CIMEF ASSURANCE • CNPS • EDLONA\n• GGA • GROUPE MEDICAL PROMETHEE\n• MCI CARE • ME SIE • MU2S-CIE\n• MUGEF-CI • MUPEMENET-CI • NSIA\n• OBEU • ODISSEY GS • OLEA • RISS\n• ROINA • SACM FATIMA • SCA INTER\n• SERENITY • SIMPARA • SUNU VIE\n• VITALIS SANTE • WILLIS MED\'INOVA\n• WILLIS TOWERS WATSON\n\n💡 **Conseil** : Présentez votre carte d\'assuré lors de votre visite.',
        followUp: ['telephone', 'horaires']
      };
    }
    
    // Pharmacies de garde
    if (lowerQuery.includes('garde') || lowerQuery.includes('nuit') || lowerQuery.includes('urgence') || lowerQuery.includes('ouvert 24')) {
      return {
        text: '🏪 **Pharmacies de garde**\n\nPour trouver une pharmacie de garde :\n\n👉 Consultez notre page **"Pharmacies de garde"** sur ce site\n\nVous pouvez filtrer par :\n• Ville (Abidjan, Cocody, Yopougon...)\n• Section/Quartier\n• Semaine du mois\n\n📞 En cas d\'urgence médicale :\n• SAMU : **185**\n• Pompiers : **180**',
        followUp: ['telephone', 'horaires']
      };
    }
    
    // Urgence
    if (lowerQuery.includes('urgence') || lowerQuery.includes('urgent') || lowerQuery.includes('nuit') || lowerQuery.includes('garde')) {
      return {
        text: '🚨 **Urgence**\n\nEn cas d\'urgence médicale, contactez :\n\n• **SAMU :** 185\n• **Pompiers :** 180\n• **Police :** 170\n\nPour les pharmacies de garde, écoutez la radio locale ou consultez les journaux.\n\n⚠️ En cas de danger vital, appelez immédiatement les secours !',
        followUp: ['telephone', 'horaires']
      };
    }
    
    // Salutations
    if (lowerQuery.includes('bonjour') || lowerQuery.includes('salut') || lowerQuery.includes('hello') || lowerQuery.includes('bonsoir')) {
      return {
        text: 'Bonjour ! 👋 Bienvenue à la Pharmacie Saint Ange Des Amis !\n\nComment puis-je vous aider aujourd\'hui ?',
        followUp: ['horaires', 'medicament', 'reservation']
      };
    }
    
    // Remerciements
    if (lowerQuery.includes('merci') || lowerQuery.includes('thanks')) {
      return {
        text: 'Je vous en prie ! 😊\n\nN\'hésitez pas si vous avez d\'autres questions.\n\nÀ bientôt à la Pharmacie Saint Ange Des Amis ! 💚',
        followUp: []
      };
    }
    
    // Au revoir
    if (lowerQuery.includes('au revoir') || lowerQuery.includes('bye') || lowerQuery.includes('à bientôt')) {
      return {
        text: 'Au revoir et à bientôt ! 👋\n\nN\'hésitez pas à revenir si vous avez des questions.\n\nPrenez soin de vous ! 💚',
        followUp: []
      };
    }
    
    // Default response
    return {
      text: '🤔 Je ne suis pas sûr de comprendre votre question.\n\nVoici ce que je peux vous aider à trouver :\n\n• Horaires d\'ouverture\n• Adresse et itinéraire\n• Comment réserver un produit\n• Disponibilité des médicaments\n• Contact téléphonique\n\nOu appelez-nous directement au **07 78 41 52 77** !',
      followUp: ['horaires', 'adresse', 'telephone', 'reservation']
    };
  };

  const handleQuickAction = (actionId) => {
    let query = '';
    switch (actionId) {
      case 'horaires':
        query = 'Quels sont vos horaires ?';
        break;
      case 'contact':
        query = 'Quelle est votre adresse et numéro de téléphone ?';
        break;
      case 'catalogue':
        query = 'Je cherche un produit, avez-vous un catalogue ?';
        break;
      case 'livraison':
        query = 'Faites-vous la livraison ?';
        break;
      case 'paiement':
        query = 'Quels sont les modes de paiement acceptés ?';
        break;
      case 'assurance':
        query = 'Quelles assurances prenez-vous en charge ?';
        break;
      default:
        query = actionId;
    }
    handleSendMessage(query);
  };

  const handleSendMessage = (text = inputValue) => {
    if (!text.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: text,
      time: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Get and add bot response after a short delay
    setTimeout(() => {
      const response = getAutomatedResponse(text);
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: response.text,
        followUp: response.followUp,
        time: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    }, 500);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Parse markdown-like formatting
  const formatMessage = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\(#catalog\)/g, '<a href="#" class="text-[#2E7D32] underline font-semibold catalog-link">$1</a>')
      .replace(/\n/g, '<br/>');
  };

  // Handle click on catalog link
  const handleMessageClick = (e) => {
    if (e.target.classList.contains('catalog-link')) {
      e.preventDefault();
      if (onNavigateToCatalog) {
        onNavigateToCatalog();
        setIsOpen(false);
      }
    }
  };

  return (
    <>
      {/* Chat Button - Green Theme */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-[9999] w-14 h-14 bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 ${isOpen ? 'hidden' : ''}`}
        aria-label="Ouvrir le chat"
        data-testid="chat-open-btn"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white animate-pulse"></span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] w-[calc(100vw-32px)] sm:w-[380px] max-w-[400px] h-[65vh] sm:h-[500px] max-h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          {/* Header - Green Theme */}
          <div className="bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] text-white p-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Assistant Pharmacie</h3>
                <p className="text-xs text-white/80">En ligne</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-9 h-9 bg-white/15 hover:bg-white/35 active:bg-white/45 rounded-full flex items-center justify-center transition-colors ring-1 ring-white/40"
              aria-label="Fermer le chat"
              data-testid="chat-close-btn"
            >
              <X className="w-5 h-5 text-white" strokeWidth={2.5} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F9FAFB] min-h-0" onClick={handleMessageClick}>
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${message.type === 'user' ? 'order-1' : 'order-2'}`}>
                  <div className={`flex items-end gap-2 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === 'user' ? 'bg-[#2E7D32]' : 'bg-[#1B5E20]'
                    }`}>
                      {message.type === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={`px-4 py-3 rounded-2xl ${
                      message.type === 'user' 
                        ? 'bg-[#2E7D32] text-white rounded-br-md' 
                        : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-200'
                    }`}>
                      <div 
                        className="text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: formatMessage(message.text) }}
                      />
                      <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                        {formatTime(message.time)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Follow-up suggestions */}
                  {message.type === 'bot' && message.followUp && message.followUp.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 ml-10">
                      {message.followUp.map((actionId) => {
                        const action = quickActions.find(a => a.id === actionId);
                        if (!action) return null;
                        return (
                          <button
                            key={actionId}
                            onClick={() => handleQuickAction(actionId)}
                            className="px-3 py-1 text-xs bg-white border border-gray-200 rounded-full text-[#2E7D32] hover:bg-[#2E7D32] hover:text-white transition-colors flex items-center gap-1"
                          >
                            {action.label}
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions - Scrollable */}
          <div className="px-3 py-2 bg-white border-t border-gray-100 flex-shrink-0">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action.id)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-[#E8F5E9] border border-[#2E7D32]/30 rounded-full text-sm text-[#1B5E20] hover:bg-[#2E7D32] hover:text-white hover:border-[#2E7D32] transition-colors whitespace-nowrap font-medium flex-shrink-0"
                  data-testid={`quick-action-${action.id}`}
                >
                  <action.icon className="w-4 h-4" />
                  {action.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1 text-center">← Faites défiler pour plus d'options →</p>
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-100 flex-shrink-0">
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
              className="flex gap-2"
            >
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Écrivez votre message..."
                className="flex-1 border-gray-200 focus:border-[#2E7D32] focus:ring-[#2E7D32]/20"
                data-testid="chat-input"
              />
              <Button
                type="submit"
                size="icon"
                className="bg-[#2E7D32] hover:bg-[#1B5E20] flex-shrink-0"
                disabled={!inputValue.trim()}
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Réponses automatiques • Pour un conseil personnalisé, appelez-nous
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PharmacyChatbot;
